import { UNARY_OPERATORS } from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/constants';
import {
  ConditionalRule,
  ConditionalRuleValueAll,
  ConditionalRuleValueConfig,
  getConditionalRuleParts,
  getJsonSchemaType,
  getOperatorDisplayName,
  JsonLogicValue,
  LogicBlockOrV2,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { WorkflowOperators } from 'dashboard/models/workflow-schema';
import { FieldSchemaType, JsonSchemaTypeToFieldType } from 'lib/fields';
import { JsonSchema } from 'lib/json-schema';

export type RulesValidity = {
  areAllRulesValid: boolean;
  // Should update validityMap to map to validity, which input is invalid, and an error message
  validityMap: Record<string, boolean>;
};

// type guard informs typescript that if isConditionalValueEmpty is true, it can one of a few "empty" types based on our checks
export function isConditionalValueEmpty(
  value: unknown,
): value is null | undefined | '' | Record<string, never> {
  // Check for null or undefined
  if (value == null) {
    return true;
  }

  // Check for empty string
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }

  // Check for empty array
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  // Check for empty object
  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return true;
  }

  // Always treat booleans as non-empty
  if (typeof value === 'boolean') {
    return false;
  }

  return false;
}

// Default rule validation function to ensure all fields are not empty
export const validateNonEmptyRule = (rule: ConditionalRule): boolean => {
  const { operator, valueLeftConfig, valueRightConfig } = getConditionalRuleParts(rule);

  if (!operator) {
    return false;
  }

  // Unary operators don't include a right value
  if (UNARY_OPERATORS.includes(operator)) {
    return valueLeftConfig?.value != null && valueRightConfig?.value == null;
  } else {
    const isNonEmptyRightConfig = Array.isArray(valueRightConfig?.value)
      ? valueRightConfig?.value.length > 0
      : valueRightConfig?.value != null;
    return valueLeftConfig?.value != null && isNonEmptyRightConfig;
  }
};

export const validRuleMapKey = (orIndex: number, andIndex: number): string => {
  return orIndex.toString() + '_' + andIndex.toString();
};

export const getRulesValidity = (
  rules: LogicBlockOrV2,
  validateRule: (rule: ConditionalRule) => boolean,
): RulesValidity => {
  let areAllRulesValid = true;
  const validityMap = rules.or.reduce(
    (map, andRule, orIndex) => {
      const andRulesValidityMap = andRule.and.reduce(
        (map, baseRule, andIndex) => {
          const key = validRuleMapKey(orIndex, andIndex);
          const val = validateRule(baseRule);
          if (!val) {
            areAllRulesValid = false;
          }
          return {
            ...map,
            [key]: val,
          };
        },
        {} as Record<string, boolean>,
      );

      return {
        ...map,
        ...andRulesValidityMap,
      };
    },
    {} as Record<string, boolean>,
  );

  return {
    areAllRulesValid,
    validityMap,
  };
};

export const generateOperatorOptions = (
  fieldSchema: JsonSchema | null,
  operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>,
  initialOperator?: WorkflowOperatorKey,
) => {
  if (fieldSchema == null) return [];
  const fieldType = JsonSchemaTypeToFieldType[getJsonSchemaType(fieldSchema)];

  const initialOperatorOption = initialOperator
    ? {
        value: initialOperator,
        label: getOperatorDisplayName(initialOperator),
      }
    : null;

  if (!fieldType) {
    return initialOperatorOption ? [initialOperatorOption] : [];
  }

  const supportedOperatorOptions =
    operatorsMap[fieldType]?.map((operator: WorkflowOperatorKey) => {
      const operatorOption = {
        value: operator,
        label: getOperatorDisplayName(operator),
      };

      // if operator label matches label for the initial operator, return value matching initial operator
      // some operators like = and == are functionally the same and map to the same label on the frontend
      // we want to return the option matching the initial value so the dropdown can mark the correct label as selected
      if (operatorOption.label === initialOperatorOption?.label) {
        return initialOperatorOption;
      } else {
        return operatorOption;
      }
    }) || [];

  // if we don't find a matching operator with the same name, add it so the dropdown is populated with a value
  if (
    initialOperatorOption &&
    !supportedOperatorOptions.some(
      (operatorOption) => initialOperatorOption.label === operatorOption.label,
    )
  ) {
    supportedOperatorOptions.push(initialOperatorOption);
  }

  return supportedOperatorOptions;
};

export const getRightHandDisplay = (
  ruleValueConfig: ConditionalRuleValueConfig | undefined,
): JsonLogicValue => {
  if (ruleValueConfig == null) {
    return '';
  }
  const { type, value, paths } = ruleValueConfig;

  switch (type) {
    case RuleValueTypes.Value: {
      return value;
    }
    case RuleValueTypes.Field: {
      return paths[0];
    }
    default: {
      return '';
    }
  }
};

export const buildRule = (
  operator: WorkflowOperatorKey,
  leftValue: JsonLogicValue,
  rightType: RuleValueTypes,
  rightValue?: JsonLogicValue,
): ConditionalRule => {
  let rightSide: ConditionalRuleValueAll;
  if (!isConditionalValueEmpty(rightValue) && rightType === RuleValueTypes.Field) {
    rightSide = { var: rightValue.toString() };
  } else if (!isConditionalValueEmpty(rightValue) && rightType === RuleValueTypes.Value) {
    rightSide = rightValue;
  }

  const rule: ConditionalRule = {
    [operator]: [
      {
        var: leftValue,
      },
    ],
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (!isConditionalValueEmpty(rightSide)) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    rule[operator].push(rightSide);
  }

  return rule;
};

export const createOperatorsMap = (operators?: WorkflowOperators) => {
  return {
    [FieldSchemaType.String]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.ContainsAnySubstring,
      WorkflowOperatorKey.ContainsNoSubstring,
      WorkflowOperatorKey.StartsWith,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Boolean]: [WorkflowOperatorKey.DoubleEquals],
    [FieldSchemaType.Date]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Number]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Integer]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.GreaterThan,
      WorkflowOperatorKey.GreaterThanOrEqual,
      WorkflowOperatorKey.LessThan,
      WorkflowOperatorKey.LessThanOrEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.Choices]: [
      WorkflowOperatorKey.DoubleEquals,
      WorkflowOperatorKey.DoesNotEqual,
      WorkflowOperatorKey.In,
      WorkflowOperatorKey.NotIn,
      WorkflowOperatorKey.IsBlank,
      WorkflowOperatorKey.IsNotBlank,
    ],
    [FieldSchemaType.MultiChoices]: [
      WorkflowOperatorKey.Contains,
      WorkflowOperatorKey.IncludesAll,
      WorkflowOperatorKey.IncludesNone,
      WorkflowOperatorKey.IncludesNotAll,
      WorkflowOperatorKey.IncludesOnly,
      WorkflowOperatorKey.IncludesExactly,
      WorkflowOperatorKey.IncludesNotExactly,
    ],
    ...operators,
  };
};
